import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { AddMerchantProductCategoryEndPoint } from '../../../Data/service/global.service';
import { HttpHelpService } from '../../../Data/service/http-help.service';

@Component({
  selector: 'app-add-merchant-category-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-merchant-category-modal.component.html',
  styleUrls: ['./add-merchant-category-modal.component.scss'],
})
export class AddMerchantCategoryModalComponent implements OnInit {
  @ViewChild('aiPreviewModal') aiPreviewModal: any;
  @ViewChild('imageViewModal') imageViewModal: any;

  form: FormGroup;
  validate = false;
  curentLang = localStorage.getItem('curentLang') || 'ar';
  merchantId = JSON.parse(localStorage.getItem('merchant_id'));

  currentImageUrl: string | null = null;
  aiPreviewUrl: string | null = null;
  aiPreviewKey: string | null = null;
  pendingPreviewKey: string | null = null;
  imageViewUrl: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private spinner: NgxSpinnerService,
    private service: HttpHelpService,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      code: new FormControl(''),
      sort_order: new FormControl(0),
      generate_ai_image: new FormControl(false),
      ai_image_prompt: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.resetForm();
  }

  private msg(ar: string, en: string, he: string): string {
    return this.curentLang === 'ar' ? ar : this.curentLang === 'en' ? en : he;
  }

  private apiMsg(res: any): string {
    return this.curentLang === 'ar'
      ? res.messageAr
      : this.curentLang === 'en'
        ? res.messageEn
        : res.messageHe;
  }

  private buildCategoryNames(): { name_ar: string; name_en: string; name_he: string } | null {
    const name = String(this.form.value.name || '').trim();
    if (!name) return null;
    return { name_ar: name, name_en: name, name_he: name };
  }

  private buildImageSaveFields() {
    const fields: any = {};
    if (this.pendingPreviewKey) {
      fields.preview_key = this.pendingPreviewKey;
    } else if (this.form.value.generate_ai_image) {
      fields.generate_ai_image = true;
      const prompt = String(this.form.value.ai_image_prompt || '').trim();
      if (prompt) fields.ai_image_prompt = prompt;
    }
    return fields;
  }

  resetForm(): void {
    this.form.reset({
      name: '',
      code: '',
      sort_order: 0,
      generate_ai_image: false,
      ai_image_prompt: '',
    });
    this.currentImageUrl = null;
    this.pendingPreviewKey = null;
    this.aiPreviewUrl = null;
    this.aiPreviewKey = null;
    this.validate = false;
  }

  openImagePreview(url: string | null | undefined): void {
    if (!url) return;
    this.imageViewUrl = url;
    this.modalService.open(this.imageViewModal, { size: 'lg', centered: true });
  }

  generateAiImage(): void {
    const name = String(this.form.value.name || '').trim();
    const prompt = String(this.form.value.ai_image_prompt || '').trim();

    if (!name && !prompt) {
      Swal.fire({
        icon: 'warning',
        title: this.msg(
          'أدخل اسم التصنيف أو برومبت لتوليد الصورة',
          'Enter a category name or prompt to generate an image',
          'הזן שם קטגוריה או פרומפט כדי ליצור תמונה',
        ),
      });
      return;
    }

    this.spinner.show();
    this.service
      .aiGenerateMerchantProductCategoryImage({
        merchant_id: Number(this.merchantId),
        name: name || undefined,
        prompt: prompt || undefined,
      })
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          const data = res.data || res;
          this.aiPreviewUrl = data.preview_url || null;
          this.aiPreviewKey = data.preview_key || null;
          if (!this.aiPreviewUrl || !this.aiPreviewKey) {
            Swal.fire({
              icon: 'error',
              title:
                this.apiMsg(res) ||
                this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
            });
            return;
          }
          this.modalService.open(this.aiPreviewModal, { size: 'lg', centered: true });
        },
        error: () => {
          this.spinner.hide();
          Swal.fire({
            icon: 'error',
            title: this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
          });
        },
      });
  }

  approveAiImage(modal?: any): void {
    if (!this.aiPreviewKey) return;

    this.pendingPreviewKey = this.aiPreviewKey;
    this.currentImageUrl = this.aiPreviewUrl;
    this.form.patchValue({ generate_ai_image: false });
    this.aiPreviewUrl = null;
    this.aiPreviewKey = null;
    if (modal) modal.close();
    Swal.fire({
      icon: 'success',
      title: this.msg(
        'تم تجهيز الصورة — احفظ التصنيف لاعتمادها',
        'Image ready — save the category to apply it',
        'התמונה מוכנה — שמור את הקטגוריה כדי להחיל אותה',
      ),
    });
  }

  save(): void {
    const names = this.buildCategoryNames();
    if (!names) {
      Swal.fire({
        icon: 'warning',
        title: this.msg(
          'يرجى ملء الحقول المطلوبة',
          'Please fill the required fields',
          'אנא מלא את השדות הנדרשים',
        ),
      });
      this.form.markAllAsTouched();
      this.validate = !this.validate;
      return;
    }

    const model = {
      ...names,
      code: String(this.form.value.code || '').trim() || undefined,
      sort_order: Number(this.form.value.sort_order) || 0,
      ...this.buildImageSaveFields(),
    };

    this.spinner.show();
    this.service.Post(AddMerchantProductCategoryEndPoint.POST, model).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res.status === 200) {
          Swal.fire({ icon: 'success', title: this.apiMsg(res) });
          const created = res.data || res.category || res;
          this.activeModal.close(created);
        } else {
          Swal.fire({ icon: 'error', title: this.apiMsg(res) });
        }
      },
      error: () => {
        this.spinner.hide();
        Swal.fire({
          icon: 'error',
          title: this.msg('فشل إضافة التصنيف', 'Failed to add category', 'הוספת הקטגוריה נכשלה'),
        });
      },
    });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
